import * as React from 'react'
import {ReactElement} from 'react'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {Nullable} from '../../../app/TypeHelpers'
import {StyledPageTitle} from '../../../components/reservations/components/CommonStyle'
import {Box} from '@mui/material'
import {AUTHENTICATION_CODE_TYPE} from '../../../CodeMaster'
import {Recipient} from './repository/NatGetTwoFactorAuthRecipients'
import {NatTwoFactorAuthRepo} from './repository/NatTwoFactorAuthRepo'
import PPRadioGroupForGuest, {GuestRadioButtonProps} from '../../../components/shared/forms/guest/PPRadioGroupForGuest'
import {GuestAuthButton, ItemHeading, WideWhiteArea} from './common/NatTwoFactorAuthCommon'
import {FullWidthColumnFlexContainer} from '../../../styledComponents/FlexComponents'
import {Colors} from '../../../styledComponents/Constants'
import {NatBaseResponseInterface} from '../common/repository/NatBaseResponseInterface'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {Location} from 'react-router-dom'

interface Props {
    twoFactorAuthRepo: NatTwoFactorAuthRepo
    location: Location
}

interface States {
    recipientsInfo: Recipient
    authType: AUTHENTICATION_CODE_TYPE
    isLoadCompleted: boolean
}

class NatGuestTwoFactorAuthSendPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, States> {

    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            recipientsInfo: {
                smsPhoneNumber: null,
                voicePhoneNumber: null,
                emailAddress: null,
            },
            authType: AUTHENTICATION_CODE_TYPE.SMS,
            isLoadCompleted: false,
        }

        this.handleChangeRecipient = this.handleChangeRecipient.bind(this)
        this.onClickAuthCodeSendButton = this.onClickAuthCodeSendButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter, twoFactorAuthRepo} = this.props

        await eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                twoFactorAuthRepo.getTwoFactorAuthRecipientsFromOwnershipTransfers.bind(twoFactorAuthRepo),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                })

            if (!response?.details) {
                return
            }

            const recipientsInfo = response?.details
            const authType = recipientsInfo?.smsPhoneNumber ?
                AUTHENTICATION_CODE_TYPE.SMS : recipientsInfo.voicePhoneNumber ?
                    AUTHENTICATION_CODE_TYPE.VOICE : AUTHENTICATION_CODE_TYPE.EMAIL

            this.setState({recipientsInfo: response?.details, authType: authType})
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    getRadioGroupDataForRecipient(): GuestRadioButtonProps[] {
        const {smsPhoneNumber, voicePhoneNumber, emailAddress} = this.state.recipientsInfo

        const createRadioGroupData = (recipientInfo: string, recipientCode: AUTHENTICATION_CODE_TYPE) =>
            ({
                label:
                    <Box color={Colors.Body}>
                        <StyledRecipientInfo>{recipientInfo}</StyledRecipientInfo>
                        <Box>{recipientCode.label}で送信する</Box>
                    </Box>,
                value: recipientCode.value,
            })

        const radioButtonProps = []
        if (smsPhoneNumber) {
            radioButtonProps.push(createRadioGroupData(
                `ご登録の電話番号 ${smsPhoneNumber}へ`,
                AUTHENTICATION_CODE_TYPE.SMS))
        }
        if (voicePhoneNumber) {
            radioButtonProps.push(createRadioGroupData(
                `ご登録の電話番号 ${voicePhoneNumber}へ`,
                AUTHENTICATION_CODE_TYPE.VOICE))
        }
        if (emailAddress) {
            radioButtonProps.push(createRadioGroupData(
                `ご登録のメールアドレス ${emailAddress}へ`,
                AUTHENTICATION_CODE_TYPE.EMAIL))
        }
        return radioButtonProps
    }

    async onClickAuthCodeSendButton(): Promise<void> {
        const {eventEmitter, twoFactorAuthRepo} = this.props
        const {authType} = this.state

        await this.props.eventEmitter.showLoading()
        try {
            let response: Nullable<NatBaseResponseInterface> = null
            switch (authType) {
                case AUTHENTICATION_CODE_TYPE.SMS:
                case AUTHENTICATION_CODE_TYPE.VOICE:
                    response = await this.props.asyncCallFunction(
                        twoFactorAuthRepo.sendAuthCodeSmsFromOwnershipTransfers.bind(
                            twoFactorAuthRepo, authType),
                        async (httpErrorOrNetworkError) => {
                            await httpErrorOrNetworkError.broadcast(eventEmitter)
                        },
                    )
                    break
                case AUTHENTICATION_CODE_TYPE.EMAIL:
                    response = await this.props.asyncCallFunction(
                        twoFactorAuthRepo.sendAuthCodeEmailFromOwnershipTransfers.bind(
                            twoFactorAuthRepo),
                        async (httpErrorOrNetworkError) => {
                            await httpErrorOrNetworkError.broadcast(eventEmitter)
                        },
                    )
                    break
            }

            if (response && response.requestError === null) {
                this.props.navigate(
                    `/guest/two-factor-auth/1/input/${authType.value}${window.location.search}`,
                    {
                        replace: true,
                        state: this.props.location.state,
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleChangeRecipient(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({authType: AUTHENTICATION_CODE_TYPE.codeToEnumConstants(event.target.value)})
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return (
            <>
                <Box height={'15px'}/>
                <StyledPageTitle>認証コードの送信</StyledPageTitle>
                <Box height={'20px'}/>
                安全にご利用いただくため、本人確認をお願いします。認証方法を選択し、認証コードを送信してください。
                <Box height={'20px'}/>
                <ItemHeading>
                    送信先
                </ItemHeading>
                <WideWhiteArea>
                    <Box height={'20px'}/>
                    <Box color={Colors.AccentDark}>
                        認証コードの送信先を選択
                    </Box>
                    <Box height={'10px'}/>
                    <PPRadioGroupForGuest
                        data={this.getRadioGroupDataForRecipient()}
                        selectValue={this.state.authType.value}
                        onChange={this.handleChangeRecipient}
                        eventKeys={['authType']}
                        eventEmitter={this.props.eventEmitter}
                    />
                    <Box height={'20px'}/>
                </WideWhiteArea>
                <Box height={'20px'}/>
                <GuestAuthButton
                    onClick={this.onClickAuthCodeSendButton}
                >
                    <FullWidthColumnFlexContainer>
                        認証コードを送信する
                    </FullWidthColumnFlexContainer>
                </GuestAuthButton>
                <Box height={'15px'}/>
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestTwoFactorAuthSendPage)

const StyledRecipientInfo: StyledDiv = styled.div`
  font-size: 12px;
  line-height: 18px;
  line-break: anywhere;
`
